/**
 * Discovery Assistant Page
 *
 * Main page component for the AI-guided discovery workflow.
 * Features a chat-centric interface with contextual sidebar.
 */

import { useEffect, useState } from 'react'
import { ArrowLeft, Plus, Loader2, Settings, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { useAIStore } from '@/stores/aiStore'
import { useDiscoveryAssistantStore } from '@/stores/discoveryAssistantStore'
import { discoveryApi, type DiscoverySession } from '@/lib/api/client'
import { ChatPanel } from './ChatPanel'
import { ContextSidebar } from './ContextSidebar'

interface DiscoveryAssistantPageProps {
  onNavigateToSettings?: () => void
  onBack?: () => void
}

export function DiscoveryAssistantPage({ onNavigateToSettings, onBack }: DiscoveryAssistantPageProps) {
  const { currentProject } = useProjectStore()
  const { currentAccount } = useAuthStore()
  const { isConfigured: hasApiKey } = useAIStore()
  const {
    session,
    isLoading,
    error,
    isWorkflowLoaded,
    loadWorkflow,
    loadSession,
    createSession,
    clearSession,
  } = useDiscoveryAssistantStore()

  // Load workflow on mount
  useEffect(() => {
    if (!isWorkflowLoaded) {
      loadWorkflow()
    }
  }, [isWorkflowLoaded, loadWorkflow])

  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false)
  const [showSessionList, setShowSessionList] = useState(false)
  const [sessions, setSessions] = useState<DiscoverySession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)

  // New session form state
  const [newTitle, setNewTitle] = useState('')
  const [newMode, setNewMode] = useState<'quick' | 'deep'>('quick')
  const [newIdea, setNewIdea] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const accountId = currentAccount?.id
  const projectId = currentProject?.id

  // Load session when currentSessionId changes
  useEffect(() => {
    if (currentSessionId && accountId && projectId && !session) {
      loadSession(accountId, projectId, currentSessionId)
    }
  }, [currentSessionId, accountId, projectId, session, loadSession])

  // Load sessions list
  const loadSessions = async () => {
    if (!accountId || !projectId) return

    setIsLoadingSessions(true)
    try {
      const data = await discoveryApi.listSessions(accountId, projectId)
      setSessions(data)
    } catch (error) {
      console.error('Failed to load sessions:', error)
    } finally {
      setIsLoadingSessions(false)
    }
  }

  // Open session list
  const handleShowSessions = () => {
    setShowSessionList(true)
    loadSessions()
  }

  // Select a session
  const handleSelectSession = (selectedSession: DiscoverySession) => {
    clearSession()
    setCurrentSessionId(selectedSession.id)
    setShowSessionList(false)
  }

  // Create new session
  const handleCreateSession = async () => {
    if (!accountId || !projectId) return

    setIsCreating(true)
    try {
      const newSession = await createSession(accountId, projectId, {
        title: newTitle || 'New Discovery',
        mode: newMode,
        ideaText: newIdea || undefined,
      })
      setCurrentSessionId(newSession.id)
      setShowNewSessionDialog(false)
      setNewTitle('')
      setNewIdea('')
    } catch (error) {
      console.error('Failed to create session:', error)
    } finally {
      setIsCreating(false)
    }
  }

  // Delete session
  const handleDeleteSession = async (sessionToDelete: DiscoverySession) => {
    if (!accountId || !projectId) return

    if (!confirm(`Delete "${sessionToDelete.title}"?`)) return

    try {
      await discoveryApi.deleteSession(accountId, projectId, sessionToDelete.id)
      setSessions(sessions.filter(s => s.id !== sessionToDelete.id))
      if (currentSessionId === sessionToDelete.id) {
        clearSession()
        setCurrentSessionId(null)
      }
    } catch (error) {
      console.error('Failed to delete session:', error)
    }
  }

  // Handle back navigation
  const handleBack = () => {
    clearSession()
    setCurrentSessionId(null)
    if (onBack) onBack()
  }

  // No project context
  if (!accountId || !projectId) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Card className="p-6 max-w-md text-center">
          <h2 className="text-lg font-semibold mb-2">No Project Selected</h2>
          <p className="text-warm-600 mb-4">
            Please select a project to start a discovery session.
          </p>
        </Card>
      </div>
    )
  }

  // No session - show welcome screen
  if (!currentSessionId && !session) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-[600px] text-center">
            <h1 className="text-3xl font-bold text-warm-900 mb-4">
              AI Discovery Assistant
            </h1>
            <p className="text-lg text-warm-600 mb-8">
              Let me guide you through 12 key questions to validate your product idea.
              I'll help you discover your market reality, understand your competitive
              landscape, design your value proposition, and validate business viability.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                onClick={() => setShowNewSessionDialog(true)}
                className="gap-2"
              >
                <Plus className="h-5 w-5" />
                Start New Discovery
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleShowSessions}
                className="gap-2"
              >
                Continue Previous Session
              </Button>
            </div>

            {!hasApiKey && (
              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Set your Claude API key in settings to enable
                  AI-powered coaching and live validation.
                </p>
                {onNavigateToSettings && (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-amber-700 mt-1"
                    onClick={onNavigateToSettings}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Go to Settings
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* New Session Dialog - outside flex container */}
        <Dialog open={showNewSessionDialog} onOpenChange={setShowNewSessionDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Start New Discovery</DialogTitle>
              <DialogDescription>
                Create a new discovery session to validate your product idea.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-warm-700">
                  Session Title
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g., B2B SaaS for HR Teams"
                  className="mt-1 w-full rounded-md border border-warm-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-warm-700">
                  Discovery Mode
                </label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setNewMode('quick')}
                    className={cn(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      newMode === 'quick'
                        ? 'border-primary bg-primary/5'
                        : 'border-warm-200 hover:border-warm-300'
                    )}
                  >
                    <div className="font-medium">Quick</div>
                    <div className="text-xs text-warm-500 mt-1">
                      20-30 min - Essential questions
                    </div>
                  </button>
                  <button
                    onClick={() => setNewMode('deep')}
                    className={cn(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      newMode === 'deep'
                        ? 'border-primary bg-primary/5'
                        : 'border-warm-200 hover:border-warm-300'
                    )}
                  >
                    <div className="font-medium">Deep</div>
                    <div className="text-xs text-warm-500 mt-1">
                      60-90 min - Thorough analysis
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-warm-700">
                  Starting Idea (optional)
                </label>
                <textarea
                  value={newIdea}
                  onChange={(e) => setNewIdea(e.target.value)}
                  placeholder="Describe your product idea in a few sentences..."
                  rows={4}
                  className="mt-1 w-full rounded-md border border-warm-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowNewSessionDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateSession}
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Start Discovery'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Session List Dialog */}
        <Dialog open={showSessionList} onOpenChange={setShowSessionList}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Your Discovery Sessions</DialogTitle>
              <DialogDescription>
                Continue where you left off
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {isLoadingSessions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-warm-500" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-warm-500">
                  <p>No previous sessions found.</p>
                  <Button
                    variant="link"
                    onClick={() => {
                      setShowSessionList(false)
                      setShowNewSessionDialog(true)
                    }}
                  >
                    Start a new discovery
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {sessions.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-warm-200 hover:bg-warm-50 cursor-pointer group"
                      onClick={() => handleSelectSession(s)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-warm-900 truncate">
                          {s.title}
                        </div>
                        <div className="text-xs text-warm-500 flex items-center gap-2">
                          <span>{s.mode === 'quick' ? 'Quick' : 'Deep'} mode</span>
                          <span className="text-warm-300">|</span>
                          <span>{s.completedQuestions.length}/12 questions</span>
                          <span className="text-warm-300">|</span>
                          <span>{new Date(s.updatedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteSession(s)
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Card className="p-6 max-w-md text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-warm-600 mb-4">{error}</p>
          <Button onClick={() => {
            clearSession()
            setCurrentSessionId(null)
          }}>
            Try Again
          </Button>
        </Card>
      </div>
    )
  }

  // Main discovery interface
  return (
    <div className="h-full flex flex-col overflow-hidden bg-warm-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-warm-200">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="h-4 w-px bg-warm-300" />
          <h1 className="font-semibold text-warm-900">
            {session?.title || 'Discovery Assistant'}
          </h1>
          {session && (
            <span className="text-xs text-warm-500 bg-warm-100 px-2 py-0.5 rounded">
              {session.mode === 'quick' ? 'Quick' : 'Deep'} Mode
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleShowSessions}
          >
            Switch Session
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-4 p-4 overflow-hidden min-h-0">
        {/* Chat panel - takes remaining space */}
        <ChatPanel
          accountId={accountId}
          projectId={projectId}
          className="flex-1 min-w-0"
        />

        {/* Context sidebar - fixed width like SmartDocsPage pattern */}
        <ContextSidebar
          accountId={accountId}
          projectId={projectId}
          className="w-80 flex-shrink-0"
        />
      </div>
    </div>
  )
}

export default DiscoveryAssistantPage
